import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { WorkerChatbot, WorkerChatbotHandle } from './Chatbot';
import { listChatSessions, ChatSessionListItem } from '../../services/api';
import { HeaderWorker } from './HeaderWorker';
import { ProfileView } from './Profile';
import filterImage from '../../assets/icons/filterImage.png';
import { useTimezoneFormatting } from 'src/hooks/useTimezoneFormatting';
import { useSidebarOpenForDashboardLayout } from 'src/hooks/useSidebarOpenForDashboardLayout';

const WorkerDashboard: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { formatToLocal } = useTimezoneFormatting();
    const chatbotRef = useRef<WorkerChatbotHandle | null>(null);
    const [sessions, setSessions] = useState<ChatSessionListItem[]>([]);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    const [loadingSessions, setLoadingSessions] = useState<boolean>(false);
    const [, setCurrentChatMessageCount] = useState<number>(0);
    const hasInitialized = useRef<boolean>(false);
    const [newChatOpenedAt, setNewChatOpenedAt] = useState<Date | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);
    const [selectedFilter, setSelectedFilter] = useState<string>('All');
    const filterDropdownRef = useRef<HTMLDivElement>(null);
    const [showProfileView, setShowProfileView] = useState<boolean>(false);
    const { isSidebarOpen, setSidebarOpen, isDesktop } = useSidebarOpenForDashboardLayout();

    // Filter sessions based on search query and date filter
    const filteredSessions = sessions.filter(session => {
        const matchesSearch = session.title.toLowerCase().includes(searchQuery.toLowerCase());

        if (!matchesSearch) return false;

        const sessionDate = new Date(session.updatedAt);
        const now = new Date();

        switch (selectedFilter) {
            case 'Today':
                return sessionDate.toDateString() === now.toDateString();
            case 'Last 7 Days':
                const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                return sessionDate >= sevenDaysAgo;
            case 'This Month':
                return sessionDate.getMonth() === now.getMonth() && sessionDate.getFullYear() === now.getFullYear();
            case 'All':
            default:
                return true;
        }
    });

    useEffect(() => {
        const load = async () => {
            try {
                setLoadingSessions(true);
                const res = await listChatSessions();
                setSessions(res.items || []);
                if (!hasInitialized.current) {
                    // Always start with a new chat after login
                    setActiveSessionId(null);
                    setNewChatOpenedAt(new Date());
                    hasInitialized.current = true;
                }
            } catch {
                setSessions([]);
            } finally {
                setLoadingSessions(false);
            }
        };
        load();
    }, [activeSessionId]);

    // Handle navigation from Profile page to start new chat
    useEffect(() => {
        if (location.state?.startNewChat) {
            // Clear the navigation state
            navigate('/worker', { replace: true, state: {} });
            // Start a new chat
            setActiveSessionId(null);
            chatbotRef.current?.startNewChat();
            setCurrentChatMessageCount(0);
            setNewChatOpenedAt(new Date());
        }
    }, [location.state, navigate]);

    // Handle clicks outside filter dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
                setShowFilterDropdown(false);
            }
        };

        if (showFilterDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilterDropdown]);

    useEffect(() => {
        if (!isDesktop) {
            setSidebarOpen(false);
        }
    }, [location.pathname, isDesktop, setSidebarOpen]);

    const toggleSidebar = () => {
        setSidebarOpen((open) => !open);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    // Session creation is handled by the Chatbot component via onFirstTurnReady callback
    // No need for complex auto-creation logic here

    return (
        <div className="worker-dashboard">
            {/* Header */}
            <HeaderWorker
                onProfileClick={() => setShowProfileView(true)}
                onMobileMenuClick={toggleSidebar}
            />

            {/* Main Content Area */}
            <div className="worker-dashboard-body">
                {/* Mobile Sidebar Overlay */}
                {isSidebarOpen && !isDesktop && (
                    <div
                        className="mobile-sidebar-overlay show"
                        onClick={closeSidebar}
                    />
                )}

                {/* Sidebar */}
                <div className={`worker-sidebar ${isSidebarOpen ? 'mobile-open' : ''}`}>
                    {/* Start New Chat Card */}
                    <div className="sidebar-start-new-chat">
                        <button
                            disabled={activeSessionId === null}
                            onClick={async () => {
                                // Always close profile and ensure a fresh chat view is shown
                                setShowProfileView(false);
                                setActiveSessionId(null);
                                chatbotRef.current?.startNewChat();
                                setCurrentChatMessageCount(0);
                                setNewChatOpenedAt(new Date());

                                // If there were messages, the prior session is already saved by Chatbot.
                                // Refresh sessions list regardless to keep the sidebar up to date.
                                try {
                                    const res = await listChatSessions();
                                    setSessions(res.items || []);
                                } catch {
                                    // Ignore error
                                }

                                // Close mobile sidebar
                                closeSidebar();
                            }}
                            className="w-100 renegade-btn-primary"
                        >
                            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.5 19L10.4853 2.01472" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M18.9853 10.5147L2 10.5" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>

                            <span>Start New Chat</span>
                        </button>
                    </div>

                    {/* Search Card */}
                    <div className="worker-sidebar-search">
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="worker-search-input"
                        />
                        <i className='iconoir-search'></i>
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="search-clear-btn"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                            className="filter-dropdown-btn"
                        >
                            <img src={filterImage} alt="Filter" className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Desktop Filter Dropdown */}
                    {showFilterDropdown && (
                        <div className="position-relative desktop-filter-dropdown">
                            <div ref={filterDropdownRef} className="search-filter-dropdown">
                                <>
                                    {['Today', 'Last 7 Days', 'This Month', 'All'].map((filter) => (
                                        <button
                                            key={filter}
                                            onClick={() => {
                                                setSelectedFilter(filter);
                                                setShowFilterDropdown(false);
                                            }}
                                            className="search-filter-dropdown-item"
                                        >
                                            <div className={`circle-icon ${selectedFilter === filter
                                                ? 'circle-icon-active'
                                                : 'circle-icon-inactive'
                                                }`} />
                                            <span className="text-14-reg">{filter}</span>
                                        </button>
                                    ))}
                                </>
                            </div>
                        </div>
                    )}

                    {/* Mobile Filter Pills */}
                    <div className="mobile-filter-pills">
                        {['All', 'Last 7 Days', 'Today', 'This Month'].map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setSelectedFilter(filter)}
                                className={`mobile-filter-pill ${selectedFilter === filter ? 'active' : ''}`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>

                    {/* Chat Sessions List */}
                    <div className="chat-sessions-list">
                        {!loadingSessions && sessions.length === 0 && (
                            <div key="temp-new-chat" onClick={() => {
                                setShowProfileView(false);
                                setActiveSessionId(null);
                                closeSidebar();
                            }} className="chat-sessions-list-item active">
                                <p className="text-13-bold grey-900 truncate">New chat</p>
                                <p className="text-12-reg grey-600 mt-1">{formatToLocal((newChatOpenedAt || new Date()).toISOString(), {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                })}</p>
                            </div>
                        )}
                        {!loadingSessions && sessions.length > 0 && filteredSessions.length === 0 && searchQuery && (
                            <div className="text-14-reg grey-600 mb-3">No chats found matching "{searchQuery}"</div>
                        )}
                        {/* Temporary new chat placeholder (not saved). Keep visible even after first message until session is created */}
                        {activeSessionId === null && sessions.length > 0 && (
                            <div key="temp-new-chat" onClick={() => {
                                setShowProfileView(false);
                                setActiveSessionId(null);
                                closeSidebar();
                            }} className="chat-sessions-list-item active">
                                <p className="text-13-bold grey-900 truncate">New chat</p>
                                <p className="text-12-reg grey-600 mt-1">{formatToLocal((newChatOpenedAt || new Date()).toISOString(), {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                })}</p>
                            </div>
                        )}
                        {filteredSessions.map((s) => (
                            <div key={s.id} onClick={() => {
                                setShowProfileView(false);
                                setActiveSessionId(s.id);
                                closeSidebar();
                            }} className={`chat-sessions-list-item ${activeSessionId === s.id ? 'active' : ''}`}>
                                <p title={s.title} className="text-13-bold grey-900 truncate">{s.title}</p>
                                <p className="text-12-reg grey-600 mt-1">{formatToLocal(s.updatedAt, {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                })}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Content */}
                <div className="worker-inner-content">
                    {/* Main Area: Profile or Chatbot */}
                    {showProfileView ? (
                        <ProfileView onBack={() => setShowProfileView(false)} />
                    ) : (
                        <WorkerChatbot
                            ref={chatbotRef}
                            sessionId={activeSessionId}
                            onMessagesChange={(c) => setCurrentChatMessageCount(c)}
                            onFirstTurnReady={async (messages, newSessionId) => {
                                // Session is created by Chatbot, just update UI state
                                if (newSessionId && activeSessionId !== newSessionId) {
                                    setActiveSessionId(newSessionId);
                                    // Refresh sessions list to show the new session
                                    try {
                                        const res = await listChatSessions();
                                        setSessions(res.items || []);
                                    } catch {
                                        // Ignore error
                                    }
                                }
                                setNewChatOpenedAt(null);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};





export default WorkerDashboard;
